import { useQuery, UseQueryResult } from '@tanstack/react-query'
import dynamicDataConfig from '@/api/config/dynamicDataConfig'
import { queryKeys } from '@/constant/common'
import { ENUM_OPERATOR } from '@/types/dynamic-data'
import {
  SCHEMA_NAME_TABLE_SAVED_VIEWS,
  TableSavedViewsRecord,
} from '@/types/table-saved-views'

/**
 * The one saved-views record for this user and grid, or `null` when they have
 * never saved a view here.
 *
 * Read one at a time: there should only ever be one record per
 * (userId, tableKey).
 *
 * Not sorted — the API only sorts by fields declared on the schema, and
 * `createdAt` is a column of the record rather than one of those, so asking
 * for it fails the whole query with a 400.
 */
const useTableSavedViews = (
  interfaceId: number | string | null | undefined,
  userId: number | undefined,
  tableKey: string,
): UseQueryResult<TableSavedViewsRecord | null> =>
  useQuery(
    [queryKeys.tableSavedViews, interfaceId, userId, tableKey],
    async () => {
      const { data } = await dynamicDataConfig.queryData(String(interfaceId), {
        schemaName: SCHEMA_NAME_TABLE_SAVED_VIEWS,
        filters: [
          {
            fieldName: 'userId',
            operator: ENUM_OPERATOR.EQUAL,
            value: String(userId),
          },
          {
            fieldName: 'tableKey',
            operator: ENUM_OPERATOR.EQUAL,
            value: tableKey,
          },
        ],
        pageNumber: 1,
        pageSize: 1,
      })

      const item = data?.items?.[0]
      if (!item) return null

      return { id: item.id, views: item.data?.views } as TableSavedViewsRecord
    },
    {
      // Both are needed to address a record at all, and the interface decides
      // which set of views is even being asked for.
      enabled: Boolean(interfaceId) && Boolean(userId),
    },
  )

export default useTableSavedViews
